import { useState, useEffect, useCallback } from "react";
import { useNotifications } from "../contexts/NotificationContext";
import PowerLevel from "./PowerLevel";
import { api } from "../services/api";
import { motion } from "framer-motion";

type PowerLevels = {
	community: {
		current: number;
		max: number;
		growth: number;
	};
	meme: {
		current: number;
		max: number;
		growth: number;
	};
};

const HeroSection = () => {
	const { addNotification } = useNotifications();
	const [isFlexing, setIsFlexing] = useState(false);
	const [clickCount, setClickCount] = useState(0);
	const [powerLevels, setPowerLevels] = useState<PowerLevels | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const fetchPowerLevels = useCallback(async () => {
		try {
			const data = await api.getPowerLevels();
			setPowerLevels((prev) => {
				// Notifica aumentos significativos
				if (prev) {
					if (data.community.current > prev.community.current) {
						addNotification({
							message: `Community Power Increased by ${data.community.growth}%! 💪`,
							type: "success",
							icon: "⚡",
						});
					}
					if (data.meme.current > prev.meme.current) {
						addNotification({
							message: `Meme Power Surging by ${data.meme.growth}%! 🚀`,
							type: "success",
							icon: "💫",
						});
					}
				}
				return data;
			});
			setError(null);
		} catch (err) {
			console.error("Error fetching power levels:", err);
			setError("Failed to load power levels");
		} finally {
			setLoading(false);
		}
	}, [addNotification]);

	useEffect(() => {
		fetchPowerLevels();
		const interval = setInterval(fetchPowerLevels, 10000);
		return () => clearInterval(interval);
	}, [fetchPowerLevels]);

	const handleBuyClick = () => {
		setIsFlexing(true);
		setTimeout(() => setIsFlexing(false), 1000);

		addNotification({
			message: "Opening SUI Swap! 💪",
			type: "info",
			icon: "🔄",
		});

		window.open(
			"https://suiai.fun/pool/0xf5650c0814de7296af30c4ab884c4756f847573f9bf465181084ca5ba2678a6c",
			"_blank",
			"noopener,noreferrer"
		);
	};

	const handleLogoClick = () => {
		setClickCount((prev) => prev + 1);
		if (clickCount >= 4) {
			addNotification({
				message: "You found a secret! Keep clicking! 👀",
				type: "success",
				icon: "🎯",
			});
			setClickCount(0);
		}
	};

	return (
		<div className='h-full flex flex-col'>
			<div className='flex items-center gap-2'>
				<h2 className='text-3xl font-black text-trump-yellow mb-6'>
					JOIN THE REVOLUTION
				</h2>
				<div className='relative group mb-6'>
					<span className='cursor-help text-white/60 hover:text-white'>ℹ️</span>
					<motion.div
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						className='absolute left-0 top-full mt-2 w-64 p-4 bg-black/90 rounded-xl shadow-xl z-10 hidden group-hover:block'>
						<p className='text-white text-sm'>
							Real community power metrics coming soon! Track our growth! 📈
						</p>
					</motion.div>
				</div>
			</div>

			<div className='flex-1 grid md:grid-cols-2 gap-6'>
				{/* Info Card */}
				<motion.div
					initial={{ opacity: 0, x: -20 }}
					animate={{ opacity: 1, x: 0 }}
					className='bg-black/30 p-6 rounded-xl border-2 border-white/10 hover:border-white/30 transition-all duration-300'>
					<h3 className='text-2xl font-bold text-white mb-4'>Why $TRUMP?</h3>
					<ul className='space-y-4'>
						<li className='flex items-center gap-3'>
							<span className='text-2xl'>🚀</span>
							<p className='text-white/80'>First meme token on SUI Network</p>
						</li>
						<li className='flex items-center gap-3'>
							<span className='text-2xl'>💪</span>
							<p className='text-white/80'>Strong and active community</p>
						</li>
						<li className='flex items-center gap-3'>
							<span className='text-2xl'>🎮</span>
							<p className='text-white/80'>Fun games and rewards</p>
						</li>
						<li className='flex items-center gap-3'>
							<span className='text-2xl'>🔒</span>
							<p className='text-white/80'>Liquidity locked forever</p>
						</li>
					</ul>
				</motion.div>

				{/* Buy Card */}
				<motion.div
					initial={{ opacity: 0, x: 20 }}
					animate={{ opacity: 1, x: 0 }}
					className='bg-black/30 p-6 rounded-xl border-2 border-white/10 hover:border-white/30 transition-all duration-300 flex flex-col'>
					<h3 className='text-2xl font-bold text-white mb-4'>Buy $TRUMP</h3>
					<p className='text-white/80 mb-6'>
						Join thousands of holders and become part of the movement!
					</p>
					<div className='flex-1 flex items-center justify-center'>
						<motion.button
							whileHover={{ scale: 1.05 }}
							whileTap={{ scale: 0.95 }}
							className='bg-trump-yellow text-black px-8 py-4 rounded-xl font-bold text-xl shadow-lg hover:bg-white transition-colors duration-300 relative group'>
							<span className='relative z-10'>BUY NOW</span>
							<motion.div
								className='absolute inset-0 bg-gradient-to-r from-trump-yellow via-white to-trump-yellow rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300'
								style={{ filter: "blur(8px)" }}
							/>
						</motion.button>
					</div>
					<div className='mt-6 grid grid-cols-2 gap-4'>
						<a
							href='#'
							target='_blank'
							rel='noopener noreferrer'
							className='flex items-center justify-center gap-2 bg-white/10 p-3 rounded-lg hover:bg-white/20 transition-colors'>
							<img src='/trump-logo.png' alt='SUI' className='w-6 h-6' />
							<span className='text-white font-medium'>SUI Swap</span>
						</a>
						<a
							href='#'
							target='_blank'
							rel='noopener noreferrer'
							className='flex items-center justify-center gap-2 bg-white/10 p-3 rounded-lg hover:bg-white/20 transition-colors'>
							<img src='/trump-logo.png' alt='Chart' className='w-6 h-6' />
							<span className='text-white font-medium'>Chart</span>
						</a>
					</div>
				</motion.div>
			</div>
		</div>
	);
};

export default HeroSection;
